import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Badge } from '@/admin/components/ui/badge';
import { Button } from '@/admin/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/admin/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/admin/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/admin/components/ui/table';
import { ScrollArea } from '@/admin/components/ui/scroll-area';
import {
  fetchTelemetryArtifact,
  type TelemetryArtifactViewResponse,
} from '@/admin/lib/api';
import { IconDownload, IconRefresh } from '@tabler/icons-react';

export const TelemetryArtifactPage: React.FC = () => {
  const params = useParams();
  const artifactId = (params as { id?: string }).id;

  const [data, setData] = useState<TelemetryArtifactViewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTimestamp, setSelectedTimestamp] = useState('');

  const load = useCallback(async () => {
    if (!artifactId) {
      setError('Missing artifact id');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await fetchTelemetryArtifact(artifactId);
      setData(result);
    } catch (err) {
      setData(null);
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to fetch telemetry artifact'
      );
    } finally {
      setLoading(false);
    }
  }, [artifactId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!data) {
      setSelectedTimestamp('');
      return;
    }

    const artifact = data.artifact;
    if (
      artifact.kind !== 'host_thread_topn' &&
      artifact.kind !== 'guest_process_topn'
    ) {
      setSelectedTimestamp('');
      return;
    }

    const snapshots = artifact.data.snapshots;
    if (snapshots.length === 0) {
      setSelectedTimestamp('');
      return;
    }

    setSelectedTimestamp(prev => {
      if (prev && snapshots.some(s => s.timestamp === prev)) {
        return prev;
      }
      return snapshots[0].timestamp;
    });
  }, [data]);

  const downloadUrl = data?.download_url;
  const contentType = data?.content_type?.trim() || '—';
  const sizeBytes =
    typeof data?.size_bytes === 'number' ? `${data.size_bytes} bytes` : '—';
  const kind = data?.artifact.kind;

  const renderBody = () => {
    if (!data) return null;

    const artifact = data.artifact;

    if (artifact.kind === 'host_thread_topn') {
      const snapshots = artifact.data.snapshots;
      const selected =
        snapshots.find(s => s.timestamp === selectedTimestamp) ||
        snapshots[0] ||
        null;

      return (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <CardTitle className="text-base">Host Thread CPU TopN</CardTitle>
              {snapshots.length > 0 && (
                <Select
                  value={selectedTimestamp}
                  onValueChange={setSelectedTimestamp}
                >
                  <SelectTrigger className="h-8 w-[280px]">
                    <SelectValue placeholder="Select snapshot timestamp" />
                  </SelectTrigger>
                  <SelectContent>
                    {snapshots.map(s => (
                      <SelectItem key={s.timestamp} value={s.timestamp}>
                        {s.timestamp}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {artifact.message && (
              <div className="mb-3 text-sm text-muted-foreground">
                {artifact.message}
              </div>
            )}
            {!selected ? (
              <div className="text-sm text-muted-foreground">
                No snapshots found.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>thread_id</TableHead>
                      <TableHead>name</TableHead>
                      <TableHead>cpu_percent</TableHead>
                      <TableHead>user_usec</TableHead>
                      <TableHead>system_usec</TableHead>
                      <TableHead>run_state</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selected.threads.map(th => (
                      <TableRow key={th.thread_id}>
                        <TableCell className="font-mono">
                          {th.thread_id}
                        </TableCell>
                        <TableCell>{th.name || '—'}</TableCell>
                        <TableCell className="font-mono">
                          {Number.isFinite(th.cpu_percent)
                            ? th.cpu_percent.toFixed(2)
                            : '—'}
                        </TableCell>
                        <TableCell className="font-mono">
                          {th.user_time_usec}
                        </TableCell>
                        <TableCell className="font-mono">
                          {th.system_time_usec}
                        </TableCell>
                        <TableCell className="font-mono">
                          {th.run_state}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      );
    }

    if (artifact.kind === 'guest_process_topn') {
      const snapshots = artifact.data.snapshots;
      const selected =
        snapshots.find(s => s.timestamp === selectedTimestamp) ||
        snapshots[0] ||
        null;

      return (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <CardTitle className="text-base">
                Guest Process CPU TopN
              </CardTitle>
              {snapshots.length > 0 && (
                <Select
                  value={selectedTimestamp}
                  onValueChange={setSelectedTimestamp}
                >
                  <SelectTrigger className="h-8 w-[280px]">
                    <SelectValue placeholder="Select snapshot timestamp" />
                  </SelectTrigger>
                  <SelectContent>
                    {snapshots.map(s => (
                      <SelectItem key={s.timestamp} value={s.timestamp}>
                        {s.timestamp}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {artifact.message && (
              <div className="mb-3 text-sm text-muted-foreground">
                {artifact.message}
              </div>
            )}
            {!selected ? (
              <div className="text-sm text-muted-foreground">
                No snapshots found.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>pid</TableHead>
                      <TableHead>comm</TableHead>
                      <TableHead>cpu_percent</TableHead>
                      <TableHead>delta_ticks</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selected.processes.map(proc => (
                      <TableRow key={`${proc.pid}:${proc.comm}`}>
                        <TableCell className="font-mono">{proc.pid}</TableCell>
                        <TableCell>{proc.comm || '—'}</TableCell>
                        <TableCell className="font-mono">
                          {Number.isFinite(proc.cpu_percent)
                            ? proc.cpu_percent.toFixed(2)
                            : '—'}
                        </TableCell>
                        <TableCell className="font-mono">
                          {proc.delta_ticks}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      );
    }

    if (artifact.kind === 'flamegraph_svg') {
      return (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">
              {artifact.data.caption || 'Flamegraph'}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {artifact.message && (
              <div className="mb-3 text-sm text-muted-foreground">
                {artifact.message}
              </div>
            )}
            <div
              className="w-full overflow-x-auto text-foreground"
              dangerouslySetInnerHTML={{ __html: artifact.data.svg }}
            />
          </CardContent>
        </Card>
      );
    }

    if (artifact.kind === 'text') {
      return (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Artifact</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {artifact.message && (
              <div className="mb-3 text-sm text-muted-foreground">
                {artifact.message}
              </div>
            )}
            <ScrollArea className="h-[420px] rounded-md border p-3">
              <pre className="whitespace-pre-wrap break-words font-mono text-sm">
                {artifact.data.snippet}
              </pre>
            </ScrollArea>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Artifact</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="text-sm text-muted-foreground">
            {artifact.message ||
              'Unsupported artifact format. Download the raw file to analyze locally.'}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="flex h-full flex-col">
      <div className="shrink-0 bg-background px-4 py-4 lg:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <span className="text-base font-semibold">
                Telemetry Artifact
              </span>
              {kind && <Badge variant="secondary">{kind}</Badge>}
            </div>
            <span className="font-mono text-xs text-muted-foreground">
              {artifactId || '—'}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              disabled={loading}
            >
              <IconRefresh className="mr-2 size-4" />
              Refresh
            </Button>
            {downloadUrl && (
              <Button asChild size="sm">
                <a href={downloadUrl} target="_blank" rel="noreferrer">
                  <IconDownload className="mr-2 size-4" />
                  Download
                </a>
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto px-4 pb-4 lg:px-6">
        <div className="flex flex-col gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Metadata</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid gap-2 text-sm sm:grid-cols-2">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">content_type</span>
                  <span className="font-mono">{contentType}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">size_bytes</span>
                  <span className="font-mono">{sizeBytes}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {loading && (
            <Card>
              <CardContent>
                <div className="flex items-center justify-center py-10">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                </div>
              </CardContent>
            </Card>
          )}

          {!loading && error && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Error</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="text-sm text-destructive">{error}</div>
              </CardContent>
            </Card>
          )}

          {!loading && !error && renderBody()}
        </div>
      </div>
    </div>
  );
};
